/**
 * Path: packages/frontend/src/store/datastore.ts
 * Task: Extract datastore logic from editorStore into a dedicated Pinia store for pure data manipulation.
 * Update: Implemented `shallowRef` to prevent devastating performance bottlenecks caused by Vue's deep reactivity on massive JSON payloads.
 */
import { defineStore } from 'pinia';
import { shallowRef } from 'vue';
import { api, type DatastoreWrapper, type ApiNode, type ApiEdge } from '@/core/services/api';
import type { Datastore, Entity } from '@/core/types/editor';
import { useAuthStore } from './auth';

interface DatastoreState {
  activeProjectId: string | null;
  data: Datastore | null;
  periods: Record<string, { label: string; weeks: number }> | null;
  isDirty: boolean;
  isLoading: boolean;
  isSaving: boolean;
}

export const useDatastoreStore = defineStore('datastore', {
  state: (): DatastoreState => ({
    activeProjectId: null,
    // CRITICAL PERFORMANCE FIX: We use shallowRef for the main data object.
    // Making thousands of nodes deeply reactive via Pinia's default reactive() wrapper causes
    // massive CPU overhead during fetching, tracking, and rendering (especially in loops).
    // Updates to specific entities inside the datastore must be done by explicitly re-assigning them or triggering saves.
    data: null,
    periods: null,
    isDirty: false,
    isLoading: false,
    isSaving: false
  }),

  getters: {
    allEntities(state): Array<Entity> {
      if (!state.data) return [];
      const unifiedList: Array<Entity> = [];
      const keys = Object.keys(state.data).filter(k => k !== 'periods');

      keys.forEach((key) => {
        const list = state.data?.[key];
        if (Array.isArray(list)) {
          list.forEach((entity) => {
            unifiedList.push({ ...entity, entityType: key });
          });
        }
      });
      return unifiedList;
    }
  },

  actions: {
    async fetchProjectDatastore(projectId: string): Promise<void> {
      this.isLoading = true;
      try {
        const response = await api.datastore.get(projectId);

        // ADR-001: Transform Nodes & Edges back to Vue-Reactive UI Object Graph
        const authStore = useAuthStore();
        const relationsDef = authStore.appData?.relations || [];
        const relMap = new Map(relationsDef.map((r: any) => [r.id, r]));

        const mappedData: Record<string, Entity[]> = {};
        const nodes = response.nodes || [];
        const edges = response.edges || [];

        // 1. Initialize Objects
        nodes.forEach(node => {
          if (!mappedData[node.type]) mappedData[node.type] = [];
          mappedData[node.type].push({
            ...node.attributes,
            id: node.id,
            title: node.title,
            entityType: node.type,
            version: node.version,
            lastUpdated: node.lastUpdated,
            updatedBy: node.updatedBy,
            rank: node.rank,
            color: node.color,
            icon: node.icon,
            system: node.system,
            history: node.history || [],
            comments: node.comments || []
          });
        });

        // 2. Attach Edges as Object Properties for Forms (v-model support)
        const entityMap = new Map<string, Entity>();
        Object.values(mappedData).forEach(list => list.forEach(e => entityMap.set(e.id, e)));

        edges.forEach(edge => {
          const source = entityMap.get(edge.source);
          const target = entityMap.get(edge.target);
          if (!source || !target) return;

          const relDef = relMap.get(edge.relationId);
          if (!relDef) return;

          // Legacy mappings for specific components (temporary until UI is fully generic)
          if (edge.relationId === 'predecessors') {
            if (!source.successorIds) source.successorIds = [];
            if (!source.successorIds.includes(target.id)) source.successorIds.push(target.id);
            if (!target.predecessorIds) target.predecessorIds = [];
            if (!target.predecessorIds.includes(source.id)) target.predecessorIds.push(source.id);
          } else if (edge.relationId === 'cairo') {
            const role = edge.meta?.raci;
            if (role) {
              if (!source.raci) source.raci = {};
              const roleKey = `${role}Ids`;
              if (!source.raci[roleKey]) source.raci[roleKey] = [];
              if (!source.raci[roleKey].includes(target.id)) source.raci[roleKey].push(target.id);

              if (!target.raci) target.raci = {};
              const revKey = `${role}ForartifactIds`;
              if (!target.raci[revKey]) target.raci[revKey] = [];
              if (!target.raci[revKey].includes(source.id)) target.raci[revKey].push(source.id);
            }
          } else {
            const outProp = relDef.isMultiple ? `${relDef.id}Ids` : `${relDef.id}Id`;
            const inProp = `inverse_${relDef.id}Ids`; // Inverses are uniformly arrays to handle 1:N and M:N gracefully

            if (relDef.isMultiple) {
              if (!source[outProp]) source[outProp] = [];
              if (!source[outProp].includes(target.id)) source[outProp].push(target.id);
            } else {
              source[outProp] = target.id;
            }
            if (!target[inProp]) target[inProp] = [];
            if (!target[inProp].includes(source.id)) target[inProp].push(source.id);
          }
        });

        // Set state using shallowRef to bypass deep reactive tracking overhead
        this.data = shallowRef(mappedData).value;
        this.periods = response.periods || null;
        this.activeProjectId = projectId;
        this.isDirty = false;
      } catch (error) {
        console.error(`DatastoreStore: Failed to fetch datastore for project ${projectId}:`, error);
        this.data = null;
        this.periods = null;
        this.activeProjectId = null;
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async saveProjectDatastore(projectId: string): Promise<void> {
      if (!this.data) return;
      this.isSaving = true;
      try {
        const authStore = useAuthStore();
        const relationsDef = authStore.appData?.relations || [];

        const nodes: ApiNode[] = [];
        const edges: ApiEdge[] = [];
        const addEdge = (e: ApiEdge) => {
          const key = `${e.source}-${e.relationId}-${e.target}-${e.meta?.raci || ''}`;
          edges.push(e);
        };

        // ADR-001: Extract relations back out of the JSON attributes
        Object.entries(this.data).forEach(([type, list]) => {
          list.forEach(ent => {
            // 1. Clean attributes
            const { id, title, entityType, version, lastUpdated, updatedBy, rank, color, icon, system, history, comments, predecessorIds, successorIds, raci, ...attrs } = ent;

            const cleanAttrs = { ...attrs };
            relationsDef.forEach((rel: any) => {
              delete cleanAttrs[`${rel.id}Id`];
              delete cleanAttrs[`${rel.id}Ids`];
              delete cleanAttrs[`inverse_${rel.id}Id`];
              delete cleanAttrs[`inverse_${rel.id}Ids`];
            });

            nodes.push({
              id, type, title: title || 'Untitled', version, lastUpdated, updatedBy, rank, color, icon, system, history, comments, attributes: cleanAttrs
            });

            // 2. Extract edges (Legacy Mappings)
            if (predecessorIds) {
              predecessorIds.forEach((tId: string) => addEdge({ source: tId, target: id, relationId: 'predecessors' }));
            }
            if (successorIds) {
              successorIds.forEach((tId: string) => addEdge({ source: id, target: tId, relationId: 'predecessors' }));
            }
            if (raci) {
              const cairoDef = relationsDef.find((r: any) => r.id === 'cairo');
              if (cairoDef && cairoDef.sourceTypes.includes(type)) {
                ['accountable', 'responsible', 'consulted', 'informed', 'omitted'].forEach(role => {
                  const ids = (raci as any)[`${role}Ids`];
                  if (ids && Array.isArray(ids)) {
                    ids.forEach((tId: string) => addEdge({ source: id, target: tId, relationId: 'cairo', meta: { raci: role } }));
                  }
                });
              }
            }

            // 3. Extract generic relations
            relationsDef.forEach((rel: any) => {
              if (rel.id === 'predecessors' || rel.id === 'cairo') return;
              if (rel.sourceTypes.includes(type)) {
                const propKey = rel.isMultiple ? `${rel.id}Ids` : `${rel.id}Id`;
                const val = ent[propKey];
                if (Array.isArray(val)) {
                  val.forEach((tId: string) => { if(tId) addEdge({ source: id, target: tId, relationId: rel.id }) });
                } else if (val) {
                  addEdge({ source: id, target: val, relationId: rel.id });
                }
              }
            });
          });
        });

        // Deduplicate edges
        const uniqueEdges: ApiEdge[] = [];
        const seenEdges = new Set<string>();
        edges.forEach(e => {
          const key = `${e.source}-${e.relationId}-${e.target}-${e.meta?.raci || ''}`;
          if (!seenEdges.has(key)) {
            seenEdges.add(key);
            uniqueEdges.push(e);
          }
        });

        const payload: DatastoreWrapper = {
          id: projectId,
          nodes: nodes,
          edges: uniqueEdges,
          periods: this.periods || undefined,
          lastModified: new Date().toISOString()
        };

        await api.datastore.save(projectId, payload);
        this.isDirty = false;
      } catch (error) {
        console.error(`DatastoreStore: Failed to save datastore for project ${projectId}:`, error);
        throw error;
      } finally {
        this.isSaving = false;
      }
    },

    setDirty(state: boolean): void {
      this.isDirty = state;
    }
  }
});
